<template>
  <!-- Card 86akqxu0h: as perguntas do formulário, já com a lógica
       condicional aplicada -- só aparece o que está visível para as
       respostas atuais. -->
  <div class="sq">
    <div
      v-for="question in shownQuestions"
      :key="question.id"
      class="sq-item"
      :class="{ 'sq-item--error': errors[question.id] }"
    >
      <div class="sq-title">
        <span class="sq-number">{{ numberOf(question.id) }}.</span>
        {{ question.title }}
        <span
          v-if="question.required"
          class="sq-required"
          :title="$t('surveys.required')"
          >*</span
        >
      </div>
      <div v-if="question.description" class="sq-description">
        {{ question.description }}
      </div>
      <!-- Retorno de 14/10: imagem da pergunta (URL assinada pela API). -->
      <img
        v-if="question.imageUrl"
        :src="question.imageUrl"
        :alt="question.title"
        class="sq-image"
      />

      <!-- Texto curto -->
      <v-text-field
        v-if="question.type === 'short_text'"
        :value="answers[question.id]"
        :disabled="disabled"
        maxlength="500"
        outlined
        dense
        hide-details="auto"
        :color="color"
        @input="set(question.id, $event)"
      />

      <!-- Texto longo -->
      <v-textarea
        v-else-if="question.type === 'long_text'"
        :value="answers[question.id]"
        :disabled="disabled"
        maxlength="5000"
        counter="5000"
        rows="4"
        auto-grow
        outlined
        hide-details="auto"
        :color="color"
        @input="set(question.id, $event)"
      />

      <!-- Escolha única -->
      <v-radio-group
        v-else-if="question.type === 'single_choice'"
        :value="answers[question.id]"
        :disabled="disabled"
        class="mt-1"
        hide-details
        @change="set(question.id, $event)"
      >
        <v-radio
          v-for="option in question.options"
          :key="option.id"
          :value="option.id"
          :color="color"
        >
          <template #label>
            <span class="sq-option">
              <img
                v-if="option.imageUrl"
                :src="option.imageUrl"
                :alt="option.label"
                class="sq-option__image"
              />
              <span>{{ option.label }}</span>
            </span>
          </template>
        </v-radio>
      </v-radio-group>

      <!-- Múltipla escolha -->
      <div v-else-if="question.type === 'multiple_choice'" class="mt-1">
        <v-checkbox
          v-for="option in question.options"
          :key="option.id"
          :input-value="(answers[question.id] || []).includes(option.id)"
          :disabled="disabled"
          :color="color"
          dense
          hide-details
          @change="toggle(question.id, option.id, $event)"
        >
          <template #label>
            <span class="sq-option">
              <img
                v-if="option.imageUrl"
                :src="option.imageUrl"
                :alt="option.label"
                class="sq-option__image"
              />
              <span>{{ option.label }}</span>
            </span>
          </template>
        </v-checkbox>
      </div>

      <!-- Lista de opções -->
      <v-select
        v-else-if="question.type === 'dropdown'"
        :value="answers[question.id]"
        :items="question.options"
        item-text="label"
        item-value="id"
        :disabled="disabled"
        :placeholder="$t('surveys.select')"
        outlined
        dense
        clearable
        hide-details="auto"
        :color="color"
        :item-color="color"
        @change="set(question.id, $event)"
      />

      <!-- Escala (1–5, 0–10...) -->
      <div v-else-if="question.type === 'scale'" class="sq-scale">
        <div class="sq-scale__buttons">
          <button
            v-for="value in scaleValues(question)"
            :key="value"
            type="button"
            class="sq-scale__btn"
            :class="{ 'sq-scale__btn--on': answers[question.id] === value }"
            :style="
              answers[question.id] === value
                ? { background: color, borderColor: color }
                : {}
            "
            :disabled="disabled"
            :aria-pressed="answers[question.id] === value ? 'true' : 'false'"
            @click="
              set(question.id, answers[question.id] === value ? null : value)
            "
          >
            {{ value }}
          </button>
        </div>
        <div
          v-if="question.scale.minLabel || question.scale.maxLabel"
          class="sq-scale__labels"
        >
          <span>{{ question.scale.minLabel }}</span>
          <span>{{ question.scale.maxLabel }}</span>
        </div>
      </div>

      <!-- Matriz (linhas × colunas) -->
      <div v-else-if="question.type === 'matrix'" class="sq-matrix">
        <table>
          <thead>
            <tr>
              <th></th>
              <th v-for="column in question.matrix.columns" :key="column.id">
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in question.matrix.rows" :key="row.id">
              <th scope="row">{{ row.label }}</th>
              <td v-for="column in question.matrix.columns" :key="column.id">
                <input
                  :type="question.matrix.multiple ? 'checkbox' : 'radio'"
                  :name="`${question.id}-${row.id}`"
                  :checked="cellChecked(question, row.id, column.id)"
                  :disabled="disabled"
                  :aria-label="`${row.label}: ${column.label}`"
                  :style="{ accentColor: color }"
                  @change="
                    pickCell(question, row.id, column.id, $event.target.checked)
                  "
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Data -->
      <v-text-field
        v-else-if="question.type === 'date'"
        :value="answers[question.id]"
        type="date"
        :disabled="disabled"
        outlined
        dense
        hide-details="auto"
        class="sq-short"
        :color="color"
        @input="set(question.id, $event)"
      />

      <!-- Telefone -->
      <v-text-field
        v-else-if="question.type === 'phone'"
        :value="answers[question.id]"
        type="tel"
        inputmode="tel"
        placeholder="(11) 98765-4321"
        maxlength="25"
        :disabled="disabled"
        outlined
        dense
        hide-details="auto"
        class="sq-short"
        :color="color"
        @input="set(question.id, $event)"
      />

      <div v-if="errors[question.id]" class="sq-error">
        {{ errors[question.id] }}
      </div>
    </div>
  </div>
</template>

<script>
import { questionsOfPage, visibleQuestionIds } from "@/shared/surveyLogic";

export default {
  name: "SurveyQuestions",

  props: {
    questions: { type: Array, default: () => [] },
    // Respostas por id da pergunta (v-model).
    value: { type: Object, default: () => ({}) },
    errors: { type: Object, default: () => ({}) },
    color: { type: String, default: "#A826E1" },
    disabled: { type: Boolean, default: false },
    // Seção (página) a mostrar; null = todas as perguntas.
    page: { type: String, default: null },
  },

  computed: {
    answers() {
      return this.value || {};
    },
    // A lógica roda no formulário inteiro (o salto atravessa páginas); a
    // página só recorta o que aparece.
    visible() {
      return visibleQuestionIds(this.questions, this.answers);
    },
    shownQuestions() {
      return questionsOfPage(this.questions, this.page).filter((question) =>
        this.visible.includes(question.id)
      );
    },
  },

  methods: {
    // Numeração contínua entre as páginas, contando só o que está visível.
    numberOf(id) {
      return this.visible.indexOf(id) + 1;
    },

    set(id, value) {
      this.$emit("input", { ...this.answers, [id]: value });
    },

    toggle(id, optionId, checked) {
      const current = new Set(this.answers[id] || []);
      if (checked) current.add(optionId);
      else current.delete(optionId);
      this.set(id, Array.from(current));
    },

    scaleValues(question) {
      const { min, max } = question.scale || { min: 1, max: 5 };
      const values = [];
      for (let value = Number(min); value <= Number(max); value += 1) {
        values.push(value);
      }
      return values;
    },

    cellChecked(question, rowId, columnId) {
      const cell = (this.answers[question.id] || {})[rowId];
      return Array.isArray(cell) ? cell.includes(columnId) : cell === columnId;
    },

    pickCell(question, rowId, columnId, checked) {
      const current = { ...(this.answers[question.id] || {}) };
      if (question.matrix.multiple) {
        const cell = new Set(current[rowId] || []);
        if (checked) cell.add(columnId);
        else cell.delete(columnId);
        current[rowId] = Array.from(cell);
      } else {
        current[rowId] = columnId;
      }
      this.set(question.id, current);
    },
  },
};
</script>

<style scoped>
.sq {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.sq-item {
  padding: 18px 20px;
  border: 1px solid var(--dk-line, #ece8f3);
  border-radius: 14px;
  background: var(--dk-surface, #fff);
}

.sq-item--error {
  border-color: var(--dk-red, #e5484d);
}

.sq-title {
  font-weight: 700;
  font-size: 1rem;
  color: var(--dk-ink, #2b2540);
  margin-bottom: 4px;
}

.sq-number {
  color: #8a8499;
  margin-right: 2px;
}

.sq-required {
  color: var(--dk-red, #e5484d);
  margin-left: 2px;
}

.sq-description {
  color: var(--dk-muted, #6b6580);
  font-size: 0.875rem;
  margin-bottom: 10px;
  white-space: pre-line;
}

.sq-image {
  display: block;
  max-width: 100%;
  max-height: 320px;
  object-fit: contain;
  border-radius: 8px;
  margin-bottom: 10px;
}

.sq-option {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.sq-option__image {
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e6e1f0;
  flex-shrink: 0;
}

.sq-short {
  max-width: 280px;
}

.sq-scale__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.sq-scale__btn {
  min-width: 42px;
  height: 42px;
  padding: 0 10px;
  border: 1px solid #d9d4e3;
  border-radius: 10px;
  background: var(--dk-surface, #fff);
  font-weight: 700;
  color: var(--dk-ink, #2b2540);
  cursor: pointer;
}

.sq-scale__btn--on {
  color: #fff;
}

.sq-scale__btn:disabled {
  cursor: default;
  opacity: 0.6;
}

.sq-scale__labels {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 0.8rem;
  color: var(--dk-muted, #6b6580);
}

.sq-matrix {
  overflow-x: auto;
  margin-top: 8px;
}

.sq-matrix table {
  border-collapse: collapse;
  min-width: 100%;
}

.sq-matrix th,
.sq-matrix td {
  padding: 8px 10px;
  text-align: center;
  font-size: 0.85rem;
  border-bottom: 1px solid #f0edf5;
}

.sq-matrix thead th {
  color: var(--dk-muted, #6b6580);
  font-weight: 600;
}

.sq-matrix tbody th {
  text-align: left;
  font-weight: 600;
  color: var(--dk-ink, #2b2540);
}

.sq-matrix input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.sq-error {
  margin-top: 8px;
  color: var(--dk-red, #e5484d);
  font-size: 0.82rem;
  font-weight: 600;
}
</style>
